import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { resolveImage, formatApiErrorDetail, formatINR } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Upload, X, ArrowLeft, ImageIcon, Calculator } from "lucide-react";

const empty = {
  name: "", sku: "", description: "", category: "Gold", purity: "9K",
  weight: "", length: "", pricing_mode: "dynamic", weight_grams: "",
  making_charge_override: "", promo_discount: "",
  mrp: "", price: "", stock: 0, featured: false, active: true, images: [],
};

function purityForCategory(category) {
  if (category === "Gold") return "9K";
  if (category === "Silver") return "925 Silver";
  return "Gold Plated";
}

function pricingModeForCategory(newCategory, prevForm) {
  if (newCategory === "GP") return "manual";
  if (prevForm.pricing_mode === "manual" && prevForm.category === "GP") return "dynamic";
  return prevForm.pricing_mode;
}

function perGramRate(category, settings) {
  if (category === "Gold") return (Number(settings.gold_rate_24k) || 0) * 9 / 24;
  if (category === "Silver") return (Number(settings.silver_rate_995) || 0) * 925 / 995;
  return 0;
}

export default function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const isEdit = !!id;
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef();

  useEffect(() => {
    if (isEdit) {
      api.get(`/products/${id}`).then(({ data }) => setForm({
        ...empty, ...data,
        weight_grams: data.weight_grams ?? "",
        making_charge_override: data.making_charge_override ?? "",
        promo_discount: data.promo_discount ?? "",
      })).catch(() => toast.error("Product not found"));
    }
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setCategory = (v) => {
    setForm((f) => ({
      ...f,
      category: v,
      pricing_mode: pricingModeForCategory(v, f),
      purity: purityForCategory(v),
    }));
  };

  const isMetal = form.category === "Gold" || form.category === "Silver";
  const isDynamic = form.pricing_mode === "dynamic" && isMetal;

  // ---- live price preview for dynamic ----
  const perGram = perGramRate(form.category, settings);
  const makingDefault = form.category === "Gold" ? Number(settings.gold_making_pct) : Number(settings.silver_making_pct);
  const makingPct = form.making_charge_override !== "" && form.making_charge_override != null
    ? Number(form.making_charge_override) : (makingDefault || 10);
  const gstPct = Number(settings.gst_pct) || 3;
  const wg = Number(form.weight_grams) || 0;
  const metalValue = wg * perGram;
  const makingCharge = metalValue * makingPct / 100;
  const gstAmount = (metalValue + makingCharge) * gstPct / 100;
  const calcPrice = metalValue + makingCharge + gstAmount; // discounted / selling price
  const discPct = form.promo_discount !== "" && form.promo_discount != null
    ? Number(form.promo_discount) : (Number(settings.default_discount_pct) || 0);
  const previewPrice = Math.round(calcPrice);
  const previewMrp = discPct > 0 ? Math.round(calcPrice / (1 - discPct / 100)) : previewPrice;

  // manual discount preview
  const manualDiscount = (() => {
    const m = parseFloat(form.mrp), p = parseFloat(form.price);
    if (!m || !p || p >= m) return 0;
    return Math.round(((m - p) / m) * 100);
  })();

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, images: [...f.images, data.path] }));
      }
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const payload = {
      name: form.name, sku: form.sku, description: form.description,
      category: form.category, purity: form.purity, length: form.length || null,
      stock: parseInt(form.stock) || 0, featured: form.featured, active: form.active,
      images: form.images, pricing_mode: isDynamic ? "dynamic" : "manual",
    };
    if (isDynamic) {
      if (!wg || wg <= 0) { setError("Enter a valid weight in grams"); return; }
      payload.weight_grams = wg;
      payload.making_charge_override = form.making_charge_override === "" ? null : Number(form.making_charge_override);
      payload.promo_discount = form.promo_discount === "" ? null : Number(form.promo_discount);
      payload.mrp = 0; payload.price = 0;
    } else {
      const m = parseFloat(form.mrp), p = parseFloat(form.price);
      if (!m || !p) { setError("Enter MRP and price"); return; }
      if (p > m) { setError("Discounted price cannot be greater than MRP"); return; }
      payload.mrp = m; payload.price = p; payload.weight = form.weight || null;
    }
    setLoading(true);
    try {
      if (isEdit) await api.put(`/admin/products/${id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(isEdit ? "Product updated" : "Product created");
      navigate("/admin/products");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="product-form-page">
      <button onClick={() => navigate("/admin/products")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-obsidian mb-4 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Products
      </button>
      <h1 className="font-serif text-3xl lg:text-4xl text-obsidian mb-6">{isEdit ? "Edit Chain" : "Add New Chain"}</h1>

      {error && <div className="bg-discount/10 border border-discount/30 text-discount text-sm px-4 py-3 mb-5 rounded-sm" data-testid="form-error">{error}</div>}

      <form onSubmit={submit} className="space-y-6">
        {/* Images */}
        <section className="bg-white border border-black/10 p-6">
          <Label className="overline text-gold-dark">Product Images</Label>
          <div className="flex flex-wrap gap-3 mt-3">
            {form.images.map((img, i) => (
              <div key={img} className="relative w-24 h-24 border border-black/10 group">
                <img src={resolveImage(img)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(i)} className="absolute -top-2 -right-2 bg-discount text-white w-6 h-6 flex items-center justify-center rounded-full" data-testid={`remove-image-${i}`}><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-24 h-24 border border-dashed border-black/30 flex flex-col items-center justify-center text-muted-foreground hover:border-gold hover:text-gold-dark transition-colors" data-testid="upload-image-btn">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Upload className="w-5 h-5 mb-1" /><span className="text-[10px]">Upload</span></>}
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={upload} className="hidden" data-testid="image-input" />
          </div>
          <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> Upload real product photos. First image is used as the cover.</p>
        </section>

        {/* Basic */}
        <section className="bg-white border border-black/10 p-6 grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label>Product Name *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} required className="mt-1.5 rounded-sm" data-testid="pf-name" /></div>
          <div><Label>SKU / Product Code *</Label><Input value={form.sku} onChange={(e) => set("sku", e.target.value)} required className="mt-1.5 rounded-sm" data-testid="pf-sku" /></div>
          <div>
            <Label>Category *</Label>
            <Select value={form.category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1.5 rounded-sm" data-testid="pf-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Gold">Gold (9K)</SelectItem>
                <SelectItem value="Silver">Silver (925)</SelectItem>
                <SelectItem value="GP">Gold Plated</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Purity</Label><Input value={form.purity} onChange={(e) => set("purity", e.target.value)} className="mt-1.5 rounded-sm" data-testid="pf-purity" /></div>
          <div className="sm:col-span-2"><Label>Length / Dimensions</Label><Input value={form.length || ""} onChange={(e) => set("length", e.target.value)} className="mt-1.5 rounded-sm" data-testid="pf-length" placeholder='e.g. 20"' /></div>
          <div className="sm:col-span-2"><Label>Description</Label><Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5 rounded-sm" data-testid="pf-description" /></div>
        </section>

        {/* Pricing */}
        <section className="bg-white border border-black/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <Label className="overline text-gold-dark">Pricing & Stock</Label>
            {isMetal && (
              <div className="flex items-center gap-2 text-xs">
                <span className={!isDynamic ? "text-muted-foreground" : "font-semibold"}>Metal Rate</span>
                <Switch checked={!isDynamic} onCheckedChange={(v) => set("pricing_mode", v ? "manual" : "dynamic")} data-testid="pf-pricing-mode" />
                <span className={isDynamic ? "text-muted-foreground" : "font-semibold"}>Manual</span>
              </div>
            )}
          </div>

          {isDynamic ? (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-4">
                <div><Label>Weight (grams) *</Label><Input type="number" step="0.01" min="0" value={form.weight_grams} onChange={(e) => set("weight_grams", e.target.value)} className="mt-1.5 rounded-sm" data-testid="pf-weight-grams" placeholder="e.g. 12.5" /></div>
                <div><Label>Making Charge % <span className="text-muted-foreground font-normal">(optional)</span></Label><Input type="number" step="0.1" min="0" value={form.making_charge_override} onChange={(e) => set("making_charge_override", e.target.value)} className="mt-1.5 rounded-sm" data-testid="pf-making-override" placeholder={`Default ${makingDefault || 10}%`} /></div>
                <div><Label>Stock *</Label><Input type="number" min="0" value={form.stock} onChange={(e) => set("stock", e.target.value)} required className="mt-1.5 rounded-sm" data-testid="pf-stock" /></div>
                <div><Label>Discount % <span className="text-muted-foreground font-normal">(off MRP)</span></Label><Input type="number" step="0.1" min="0" max="99" value={form.promo_discount} onChange={(e) => set("promo_discount", e.target.value)} className="mt-1.5 rounded-sm" data-testid="pf-promo" placeholder={`Default ${settings.default_discount_pct ?? 10}%`} /></div>
              </div>

              {/* Live preview */}
              <div className="bg-secondary/60 border border-gold/30 p-4 rounded-sm" data-testid="pf-price-preview">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-obsidian mb-3"><Calculator className="w-3.5 h-3.5 text-gold-dark" /> Auto-Calculated Price ({form.category === "Gold" ? "9K" : "925"} @ {formatINR(perGram)}/g)</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <PreviewCell label="Metal Value" value={formatINR(metalValue)} />
                  <PreviewCell label={`Making (${makingPct}%)`} value={formatINR(makingCharge)} />
                  <PreviewCell label={`GST (${gstPct}%)`} value={formatINR(gstAmount)} />
                  <PreviewCell label="Discounted Price" value={formatINR(previewPrice)} accent />
                </div>
                {discPct > 0 && <p className="text-xs text-muted-foreground mt-3">MRP <span className="line-through">{formatINR(previewMrp)}</span> → Discounted <strong className="text-obsidian">{formatINR(previewPrice)}</strong> · <span className="text-discount font-semibold">{Math.round(discPct)}% OFF</span></p>}
              </div>
            </div>
          ) : (
            <div>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div><Label>Weight <span className="text-muted-foreground font-normal">(display)</span></Label><Input value={form.weight || ""} onChange={(e) => set("weight", e.target.value)} className="mt-1.5 rounded-sm" data-testid="pf-weight" placeholder="e.g. 9 g" /></div>
                <div><Label>Stock *</Label><Input type="number" min="0" value={form.stock} onChange={(e) => set("stock", e.target.value)} required className="mt-1.5 rounded-sm" data-testid="pf-stock" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>MRP (₹) *</Label><Input type="number" min="0" value={form.mrp} onChange={(e) => set("mrp", e.target.value)} className="mt-1.5 rounded-sm" data-testid="pf-mrp" /></div>
                <div><Label>Discounted Price (₹) *</Label><Input type="number" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-1.5 rounded-sm" data-testid="pf-price" /></div>
              </div>
              <div className="mt-4 inline-flex items-center gap-2 bg-secondary px-4 py-2 rounded-sm">
                <span className="text-sm text-muted-foreground">Calculated Discount:</span>
                <span className="font-bold text-discount" data-testid="pf-discount-preview">{manualDiscount}% OFF</span>
              </div>
            </div>
          )}
        </section>

        {/* Flags */}
        <section className="bg-white border border-black/10 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div><p className="font-medium">Featured Product</p><p className="text-xs text-muted-foreground">Show on homepage featured collection</p></div>
            <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="pf-featured" />
          </div>
          <div className="flex items-center justify-between border-t border-black/5 pt-4">
            <div><p className="font-medium">Active</p><p className="text-xs text-muted-foreground">Visible in the catalogue</p></div>
            <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="pf-active" />
          </div>
        </section>

        <div className="flex gap-3">
          <button type="submit" disabled={loading} className="inline-flex items-center gap-2 bg-gold text-obsidian font-semibold px-8 py-3 hover:bg-gold-light transition-colors disabled:opacity-50 rounded-sm" data-testid="save-product-btn">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} {isEdit ? "Update Product" : "Create Product"}
          </button>
          <button type="button" onClick={() => navigate("/admin/products")} className="px-8 py-3 border border-black/20 font-semibold hover:bg-secondary transition-colors rounded-sm">Cancel</button>
        </div>
      </form>
    </div>
  );
}

const PreviewCell = ({ label, value, accent }) => (
  <div className={accent ? "bg-obsidian text-white p-2.5 rounded-sm" : ""}>
    <p className={`text-[10px] uppercase tracking-wider ${accent ? "text-gold" : "text-muted-foreground"}`}>{label}</p>
    <p className={`font-bold ${accent ? "text-white" : "text-obsidian"}`}>{value}</p>
  </div>
);
